import { X, Bell, CheckCheck, Clock } from 'lucide-react';
import { useEffect, useState } from 'react';

interface NotificationInfo {
    id: number;
    title: string;
    text: string;
    createdAt: string;
    status: number;
    type: number;
    target: number;
}

interface NotificationListProps {
  notifications: NotificationInfo[],
  isLoading: boolean,
  onClose?: () => void;
  onDelete?: (id: number) => void;
  onMarkAsRead?: (id: number) => void;
  onMarkAllAsRead?: () => void;
}

export default function NotificationTable({
  notifications,
  isLoading,
  onClose,
  onDelete,
  onMarkAsRead,
  onMarkAllAsRead
}: NotificationListProps) {

  const unreadCount = notifications.filter(n => !n.status).length;

  const handleDelete = (id: number) => {
  };

  const handleMarkAsRead = (id: number) => {
  };

  const handleMarkAllAsRead = () => {
  };

  const getTypeIcon = (type: number) => {
    const icons = {
      0: <Bell size={20} className="text-white" />,
      1: <CheckCheck size={20} className="text-white" />,
      2: <Clock size={20} className="text-white" />
    };
    return icons[type as keyof typeof icons] || icons[0];
  };

  const getBorderColor = (type: number) => {
    const colors = {
      2: 'border-indigo-500',
      0: 'border-green-500',
      1: 'border-yellow-500',
    };
    return colors[type as keyof typeof colors] || colors[0];
  };

  const getBgColor = (type: number) => {
    const colors = {
      2: 'bg-indigo-500',
      0: 'bg-green-500',
      1: 'bg-yellow-500',
    };
    return colors[type as keyof typeof colors] || colors[0];
  };

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  return (
    <div className="flex items-start justify-center">
      <div className="w-full bg-gray-700 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-500">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-2xl shadow-lg">
              <Bell size={28} className="text-white" />
            </div>
            <div className="ml-2">
              <h5 className="mb-0 text-xl font-bold text-white">Notification</h5>
              <small className="text-gray-200">
                {unreadCount > 0 ? `${unreadCount} unread messages` : 'All caught up!'}
              </small>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-200 transition-colors hover:text-white hover:bg-gray-600 rounded-lg"
          >
            <X size={28} />
          </button>
        </div>

        {/* Actions Bar */}
        {/* {unreadCount > 0 && (
          <div className="px-6 py-3 border-b border-purple-500">
            <button
              onClick={handleMarkAllAsRead}
              className="flex items-center gap-2 p-0 text-purple-200 transition-colors hover:text-white"
            >
              <CheckCheck size={18} />
              <span className="text-white ml-1">Mark all as read</span>
            </button>
          </div>
        )} */}

        {/* Notification List */}
        <div className="px-6 py-4 max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-gray-300 border-t-white rounded-full animate-spin"></div>
            </div>
          ) : notifications.length > 0 ? (
            <div className="flex flex-col gap-4">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`flex items-start gap-3 p-4 rounded-2xl border-l-4 ${getBorderColor(notification.type)} bg-gray-600 bg-opacity-50 ${notification.status != 0 ? 'opacity-70' : 'opacity-100'
                    } transition-all duration-200 hover:bg-opacity-70`}
                >
                  <div className={`p-2.5 ${getBgColor(notification.type)} rounded-xl flex-shrink-0`}>
                    {getTypeIcon(notification.type)}
                  </div>

                  <div className="flex-1 ml-1">
                    <p className="text-white text-base font-semibold leading-relaxed mb-1">
                      {notification.title.toLocaleUpperCase()}
                    </p>
                    <p className="mb-2 text-white text-sm leading-snug">
                      {notification.text}
                    </p>
                    <div className="flex items-center gap-2">
                      <Clock size={14} className="text-gray-300" />
                      <small className="text-gray-300">{formatDateTime(notification.createdAt)}</small>
                      {!notification.status && (
                        <span className="ml-2 px-2 py-0.5 text-xs font-semibold text-gray-900 bg-gray-300 rounded-full">
                          New
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-shrink-0 gap-2">
                    {notification.status == 1 && (
                      <button
                        onClick={() => handleMarkAsRead(notification.id)}
                        className="p-2 text-gray-300 transition-colors hover:text-white hover:bg-gray-600 rounded-lg"
                        title="Mark as read"
                      >
                        <CheckCheck size={18} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-20 h-20 mb-4 bg-yellow-600 rounded-full">
                <Bell size={40} className="text-gray-300" />
              </div>
              <h6 className="mb-2 text-lg font-bold text-white">No notifications</h6>
              <small className="text-gray-300">You're all caught up!</small>
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="text-center p-4 border-t border-gray-500 bg-gray-800 bg-opacity-30">
            <small className="text-gray-300">
              Showing {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
            </small>
          </div>
        )}
      </div>
    </div>
  );
}